import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { getPaddockById } from '../../services/farmMapping/getPaddockById'
import { getPaddocks } from '../../services/farmMapping/getPaddocks'
import { paddocks as paddocksService } from '../../services/farmMapping/savePaddocks'
import { queryKeys } from './queryKeys'

export const paddocksQueryOptions = (farmId: string | undefined) =>
  queryOptions({
    queryKey: queryKeys.paddocks(farmId),
    queryFn: ({ signal }) => getPaddocks({ farmId: farmId as string, signal }),
  })

export const paddocksByIdsQueryOptions = (ids: string[] | undefined) =>
  queryOptions({
    queryKey: queryKeys.paddocksByIds(ids),
    queryFn: ({ signal }) => getPaddockById({ id: ids, signal }),
  })

export const usePaddocks = ({ farmId }: { farmId: string | undefined }) =>
  useQuery({
    ...paddocksQueryOptions(farmId),
    enabled: !!farmId,
  })

// Module-level: an inline select would mint a new array identity on every render, and the
// allotment derivation effects key off exactly that.
const byTotalHectaresDesc = <T extends { totalHectares?: number }>(paddocks: T[]): T[] =>
  [...paddocks].sort((a, b) => (b.totalHectares ?? 0) - (a.totalHectares ?? 0))

export const usePaddocksSortedByArea = ({ farmId }: { farmId: string | undefined }) =>
  useQuery({
    ...paddocksQueryOptions(farmId),
    enabled: !!farmId,
    select: byTotalHectaresDesc,
  })

export const usePaddocksByIds = ({ ids }: { ids: string[] | undefined }) =>
  useQuery({
    ...paddocksByIdsQueryOptions(ids),
    enabled: !!ids?.length,
  })

// The by-ids entries and the year-keyed loteo list are not prefixed by paddocks(farmId), so no
// cascade reaches them: each root has to be busted by name.
const invalidatePaddockReads = (queryClient: QueryClient, farmId: string | undefined) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.paddocks(farmId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.paddocksByIdsRoot })
  queryClient.invalidateQueries({ queryKey: queryKeys.farmSubdivisionsRoot(farmId) })
  // QU geometry, hints and the cross-year holistic plan lineage derive from the paddocks server-side. Unconditional rather than gated on
  // quConfig.enabled: a farm that never reads them has nothing cached under these keys.
  queryClient.invalidateQueries({ queryKey: queryKeys.quantificationUnitsRoot(farmId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.quHintsRoot(farmId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.holisticPlanIssuesRoot(farmId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.projectArea(farmId) })
}

// These writes run inside the caller's processLoading wrapper, which already surfaces failures
// through the loading modal — letting the global handler fire too would double-report.
const suppressGlobalError = { suppressGlobalError: true }

type CreatePaddocksInput = {
  farm: { id: string }
  years: unknown[]
  paddocks: unknown[]
}

// Defaults off: only a caller that rebuilds these roots itself, after its whole write sequence
// settles, may opt out.
type InvalidationOptions = { skipInvalidation?: boolean }

export const useCreatePaddocks = ({ skipInvalidation = false }: InvalidationOptions = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePaddocksInput) => paddocksService.createPaddocks(input),
    meta: suppressGlobalError,
    // The created rows carry the farm they landed in; the variables are the fallback for the
    // empty-batch short circuit, which resolves to an empty array.
    onSuccess: (created: { farmId: string }[], variables) => {
      if (skipInvalidation) return
      invalidatePaddockReads(queryClient, created?.[0]?.farmId ?? variables.farm?.id)
    },
  })
}

export const useUpdatePaddocks = ({ skipInvalidation = false }: InvalidationOptions = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { paddocks: unknown[]; farmId: string }) =>
      paddocksService.updatePaddocksFeatures(input),
    meta: suppressGlobalError,
    onSuccess: (_result, variables) => {
      if (skipInvalidation) return
      invalidatePaddockReads(queryClient, variables.farmId)
    },
  })
}

// farmId is carried in the variables purely to key the invalidation: the DELETE URL identifies the
// paddock alone.
export const useDeletePaddock = ({ skipInvalidation = false }: InvalidationOptions = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, userRole }: { id: string; userRole: string; farmId: string | undefined }) =>
      paddocksService.deletePaddocks({ id, userRole }),
    meta: suppressGlobalError,
    onSuccess: (_result, variables) => {
      if (skipInvalidation) return
      invalidatePaddockReads(queryClient, variables.farmId)
      // The handler dissociates the paddock from its paddocks_usage metrics, and useLockedPaddocks
      // reads that separate top-level root — without this the lock badges act on a stale lock set.
      queryClient.invalidateQueries({ queryKey: queryKeys.metricEventsRoot })
    },
  })
}
